/**
 * Tu dominio de envío (Amazon SES) — S82
 *
 * Aquí el dueño del negocio conecta SU dominio para que los correos salgan a
 * su nombre. Toda la pantalla está escrita para alguien que nunca ha tocado
 * un panel de DNS: no dice «registro DKIM», dice «tres renglones que hay que
 * copiar», y explica dónde se copian.
 *
 * Dos reglas que no se pueden relajar:
 *  1. El estado NUNCA se inventa. El botón «Comprobar» le pregunta a Amazon.
 *  2. Si el tenant trae su propio proveedor de correo, esta sección se apaga
 *     sola y se lo dice — no le pedimos que configure algo que no va a usar.
 */
import { useState, useEffect } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import HelpTooltip from '@/components/help/HelpTooltip';
import {
  Globe, Copy, Check, RefreshCw, ShieldCheck, Clock,
  AlertTriangle, Trash2, Wand2,
} from 'lucide-react';

const ESTADOS = {
  verificado: { texto: 'Verificado', clase: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icono: ShieldCheck },
  pendiente: { texto: 'Esperando a Amazon', clase: 'bg-amber-50 text-amber-700 border-amber-200', Icono: Clock },
  temporal: { texto: 'Esperando a Amazon', clase: 'bg-amber-50 text-amber-700 border-amber-200', Icono: Clock },
  fallido: { texto: 'No encontró los renglones', clase: 'bg-red-50 text-red-700 border-red-200', Icono: AlertTriangle },
};

function Copiable({ texto, etiqueta }) {
  const [copiado, setCopiado] = useState(false);
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <code className="flex-1 min-w-0 truncate text-[11px] bg-[#0F172A] text-[#E2E8F0] rounded-md px-2 py-1.5 font-mono">
        {texto}
      </code>
      <button
        type="button"
        aria-label={`Copiar ${etiqueta}`}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(texto);
            setCopiado(true);
            setTimeout(() => setCopiado(false), 1500);
          } catch { toast.error('No se pudo copiar. Selecciónalo y cópialo a mano.'); }
        }}
        className="shrink-0 p-1.5 rounded-md border border-[#E2E8F0] text-[#64748B] hover:text-[#0F172A] hover:border-[#CBD5E1]"
      >
        {copiado ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

export default function SesDominioPanel() {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [comprobando, setComprobando] = useState(false);
  // Lo que el backend averiguó mirando el DNS: si los renglones están bien y
  // solo falta que Amazon pase, o si están mal escritos y esperar no sirve.
  const [pista, setPista] = useState(null);
  const [form, setForm] = useState({
    dominio: '', from_email: '', from_name: '',
    reply_to_email: '', unsubscribe_email: '',
  });

  const cargar = async () => {
    try {
      const { data } = await api.get('/tenant/email/ses');
      setDatos(data);
      if (data.identidad) {
        setForm({
          dominio: data.identidad.dominio || '',
          from_email: data.identidad.from_email || '',
          from_name: data.identidad.from_name || '',
          reply_to_email: data.identidad.reply_to_email || '',
          unsubscribe_email: data.identidad.unsubscribe_email || '',
        });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setCargando(false); }
  };
  useEffect(() => { cargar(); }, []);

  const guardar = async () => {
    if (!form.dominio.trim()) { toast.error('Escribe tu dominio primero.'); return; }
    setGuardando(true);
    try {
      const { data } = await api.put('/tenant/email/ses', form);
      if (data.automatico?.automatico) {
        toast.success(`Listo. Pusimos los ${data.automatico.puestos} renglones por ti.`);
      } else {
        toast.success('Dominio dado de alta. Ahora copia los tres renglones.');
      }
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setGuardando(false); }
  };

  const comprobar = async () => {
    setComprobando(true);
    try {
      const { data } = await api.post('/tenant/email/ses/comprobar');
      setPista(data.estado === 'verificado' ? null : (data.pista ? data : null));
      if (data.estado === 'verificado') toast.success(data.mensaje || 'Tu dominio quedó verificado.');
      else if (data.problema) toast.error(data.pista);
      else toast.message(data.mensaje || 'Todavía no.');
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setComprobando(false); }
  };

  const quitar = async () => {
    try {
      await api.delete('/tenant/email/ses');
      toast.success('Dominio desconectado.');
      setForm({ dominio: '', from_email: '', from_name: '', reply_to_email: '', unsubscribe_email: '' });
      await cargar();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (cargando) return <p className="text-xs text-[#94A3B8] py-8 text-center">Cargando...</p>;

  // El tenant trajo su propio proveedor: aquí no hay nada que configurar.
  if (datos && !datos.disponible) {
    return (
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
        <div className="flex items-start gap-3">
          <Globe className="w-5 h-5 text-[#94A3B8] mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-[#0F172A]">
              {datos.proveedor_propio
                ? 'Estás enviando con tu propio proveedor'
                : datos.por_plan
                  ? 'Tu plan no incluye el correo de CavernicolApp'
                  : 'Todavía no disponible'}
            </p>
            <p className="text-xs text-[#64748B] mt-1 leading-relaxed">{datos.motivo}</p>
            {datos.proveedor_propio && (
              <p className="text-xs text-emerald-700 mt-2">
                Tus envíos no tienen tope: pagas tu servicio directamente y nosotros no te contamos correos.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const ident = datos?.identidad;
  const estado = ESTADOS[ident?.estado] || ESTADOS.pendiente;
  const EstadoIcono = estado.Icono;

  return (
    <div className="space-y-4">
      {/* ── Qué es esto, en una frase ─────────────────────────────── */}
      <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
        <p className="text-sm font-medium text-[#0F172A] flex items-center gap-2">
          <Globe className="w-4 h-4 text-[#F72585]" /> Tu dominio de envío
        </p>
        <p className="text-xs text-[#64748B] mt-1.5 leading-relaxed">
          Conecta tu dominio para que tus correos salgan a tu nombre. Quien los reciba verá
          <strong className="text-[#0F172A]"> hola@tunegocio.com</strong>, no a CavernicolApp.
          Eso es lo que hace que lleguen a la bandeja de entrada y no a spam.
        </p>
      </div>

      {/* ── Paso 1: el dominio ────────────────────────────────────── */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm font-semibold text-[#0F172A]">1. ¿Cuál es tu dominio?</p>
          {ident && (
            <span className={`text-[11px] px-2.5 py-1 rounded-full border font-medium flex items-center gap-1.5 ${estado.clase}`}>
              <EstadoIcono className="w-3.5 h-3.5" /> {estado.texto}
            </span>
          )}
        </div>

        <div>
          <label className="text-xs font-medium text-[#475569] flex items-center gap-1">
            Dominio
            <HelpTooltip text="Es la parte que va después de la arroba en tu correo. Si tu correo es hola@tallerlopez.com, tu dominio es tallerlopez.com. Sin www y sin https." />
          </label>
          <Input
            value={form.dominio}
            onChange={(e) => setForm({ ...form, dominio: e.target.value })}
            placeholder="tunegocio.com"
            className="text-xs h-9 mt-1"
            data-testid="ses-dominio"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-[#475569] flex items-center gap-1">
              Correo de salida
              <HelpTooltip text="La dirección desde la que salen tus correos. Tiene que ser de tu propio dominio: si pones una de Gmail o Hotmail, el correo sale sin firma y se va derecho a spam." />
            </label>
            <Input value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })}
                   placeholder="hola@tunegocio.com" className="text-xs h-9 mt-1" data-testid="ses-from" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#475569] flex items-center gap-1">
              Nombre que se ve
              <HelpTooltip text="El nombre que aparece en la bandeja de quien recibe, antes del correo. Normalmente el de tu negocio: «Taller López»." />
            </label>
            <Input value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })}
                   placeholder="Taller López" className="text-xs h-9 mt-1" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#475569] flex items-center gap-1">
              Correo de contacto
              <HelpTooltip text="A dónde llegan las respuestas cuando alguien contesta tu correo. Suele ser el buzón que sí lees todos los días." />
            </label>
            <Input value={form.reply_to_email} onChange={(e) => setForm({ ...form, reply_to_email: e.target.value })}
                   placeholder="contacto@tunegocio.com" className="text-xs h-9 mt-1" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#475569] flex items-center gap-1">
              Correo de bajas
              <HelpTooltip text="A dónde llegan las peticiones de quien ya no quiere recibir tus correos. La ley obliga a ofrecer una forma de darse de baja, y atenderla rápido es lo que evita que te marquen como spam." />
            </label>
            <Input value={form.unsubscribe_email} onChange={(e) => setForm({ ...form, unsubscribe_email: e.target.value })}
                   placeholder="bajas@tunegocio.com" className="text-xs h-9 mt-1" />
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          <Button onClick={guardar} disabled={guardando}
                  className="bg-[#0F172A] text-white text-xs gap-1.5" data-testid="ses-guardar">
            <Wand2 className="w-3.5 h-3.5" />
            {guardando ? 'Dando de alta...' : ident ? 'Guardar cambios' : 'Conectar mi dominio'}
          </Button>
          {ident && (
            <Button onClick={quitar} variant="outline" className="text-xs gap-1.5 text-red-600 border-red-200">
              <Trash2 className="w-3.5 h-3.5" /> Desconectar
            </Button>
          )}
        </div>
      </div>

      {/* ── Paso 2: los tres renglones ────────────────────────────── */}
      {ident && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-4">
          <p className="text-sm font-semibold text-[#0F172A]">2. Copia estos tres renglones</p>
          <p className="text-xs text-[#64748B] leading-relaxed">
            Son la prueba de que ese dominio es tuyo y de que nos autorizas a enviar en tu nombre.
            Se capturan donde compraste tu dominio (GoDaddy, Cloudflare, Namecheap, Hostinger...),
            en la sección que se llama <strong className="text-[#0F172A]">DNS</strong> o
            <strong className="text-[#0F172A]"> Administrar registros</strong>.
            Ahí le das a «Agregar registro», eliges el tipo <strong>CNAME</strong> y pegas
            el nombre y el valor. Son tres, uno por uno.
          </p>
          <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3">
            <p className="text-[11px] text-[#475569] leading-relaxed">
              <strong>Ojo con el nombre:</strong> algunos proveedores ya escriben tu dominio
              por su cuenta —lo verás en gris junto al campo— y otros esperan que lo pongas
              tú. Abajo te damos las dos versiones de cada nombre; usa la que corresponda a
              tu proveedor. Si te equivocas, tu dominio queda escrito dos veces y el registro
              no sirve; el botón de comprobar te lo va a decir.
            </p>
          </div>

          <div className="space-y-3">
            {(ident.registros || []).map((r, i) => (
              <div key={i} className="border border-[#E2E8F0] rounded-lg p-3 space-y-2">
                <p className="text-[11px] font-semibold text-[#94A3B8] uppercase tracking-wide">
                  Renglón {i + 1} — tipo CNAME
                </p>
                {/* Los proveedores no se ponen de acuerdo con el nombre, y
                    esa diferencia dejó a un cliente esperando horas: unos
                    añaden el dominio solos y otros lo esperan escrito. Se
                    muestran las dos formas, con la más común primero. */}
                <div>
                  <p className="text-[11px] text-[#64748B] mb-1">
                    Nombre <span className="text-[#94A3B8]">— si tu proveedor ya pone tu dominio solo
                    (SiteGround, cPanel, Hostinger, Plesk)</span>
                  </p>
                  <Copiable texto={r.nombre_corto || r.nombre} etiqueta={`nombre corto ${i + 1}`} />
                </div>
                <div>
                  <p className="text-[11px] text-[#64748B] mb-1">
                    Nombre <span className="text-[#94A3B8]">— si te pide el nombre completo
                    (Cloudflare, GoDaddy, Route 53)</span>
                  </p>
                  <Copiable texto={r.nombre} etiqueta={`nombre completo ${i + 1}`} />
                </div>
                <div>
                  <p className="text-[11px] text-[#64748B] mb-1">Apunta a</p>
                  <Copiable texto={r.valor} etiqueta={`valor ${i + 1}`} />
                </div>
              </div>
            ))}
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-[11px] text-amber-900 leading-relaxed">
              <strong>Si usas Cloudflare:</strong> deja la nubecita en gris, no en naranja.
              Con la nube naranja encendida, Cloudflare contesta por ti y la firma de tus
              correos deja de servir.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={comprobar} disabled={comprobando} variant="outline"
                    className="text-xs gap-1.5" data-testid="ses-comprobar">
              <RefreshCw className={`w-3.5 h-3.5 ${comprobando ? 'animate-spin' : ''}`} />
              {comprobando ? 'Preguntando a Amazon...' : 'Comprobar si ya quedó'}
            </Button>
            {ident.ultima_revision && (
              <span className="text-[11px] text-[#94A3B8]">
                Última revisión: {new Date(ident.ultima_revision).toLocaleString('es-MX')}
              </span>
            )}
          </div>

          {/* La pista va ANTES del mensaje de Amazon: si los renglones están
              mal escritos, «espera hasta 72 horas» es un consejo que no
              arregla nada y hace perder la tarde. */}
          {pista?.pista && (
            <div className={`rounded-lg p-3 border ${pista.problema
              ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}
              data-testid="ses-pista">
              <p className={`text-xs leading-relaxed ${pista.problema
                ? 'text-red-800' : 'text-emerald-800'}`}>
                {pista.problema === 'dominio_duplicado' && <strong>Hay algo mal escrito. </strong>}
                {pista.pista}
              </p>
            </div>
          )}

          {ident.mensaje && !pista?.problema && (
            <p className="text-xs text-[#64748B] leading-relaxed bg-[#F8FAFC] rounded-lg p-3">
              {ident.mensaje}
            </p>
          )}
        </div>
      )}

      {/* ── Cupo ──────────────────────────────────────────────────── */}
      {ident && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <p className="text-sm font-semibold text-[#0F172A] flex items-center gap-1">
            Tus envíos este mes
            <HelpTooltip text="Los correos que puedes mandar al mes con el servicio incluido en tu plan. Cuentan igual los de campañas y los avisos automáticos. Si necesitas más, se amplía desde tu plan; si conectas tu propio proveedor de correo, este tope desaparece." />
          </p>
          <div className="mt-3">
            <div className="flex items-baseline justify-between text-xs">
              <span className="text-[#0F172A] font-semibold">
                {ident.enviados} de {ident.cupo}
              </span>
              <span className="text-[#64748B]">Te quedan {ident.restantes}</span>
            </div>
            <div className="mt-1.5 h-2 bg-[#E2E8F0] rounded-full overflow-hidden">
              <div className="h-full bg-[#F72585] rounded-full transition-all"
                   style={{ width: `${Math.min(100, ident.cupo ? (ident.enviados / ident.cupo) * 100 : 0)}%` }} />
            </div>
          </div>
          {ident.bloqueado && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-xs text-red-800 leading-relaxed">
                <strong>Tus envíos están detenidos.</strong> {ident.motivo_bloqueo}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
